import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { GIFT_AMOUNTS } from "../data";
import { api, formatApiError, useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

function GiftCardPreview({ amount, recipientName, senderName, message }) {
  return (
    <motion.div
      whileHover={{ rotateY: -10, rotateX: 5 }}
      transition={{ type: "spring", stiffness: 180, damping: 18 }}
      className="rounded-2xl p-[1px] bg-gradient-to-br from-amber-400/70 via-yellow-200/25 to-transparent shadow-[0_30px_80px_rgba(212,175,55,0.18)]"
      style={{ transformStyle: "preserve-3d" }}
      data-testid="gift-card-preview"
    >
      <div className="rounded-2xl bg-[#101013] p-8 aspect-[8/5] flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/15 blur-[80px]" />
        <div className="flex justify-between items-start relative">
          <span className="font-display font-bold uppercase tracking-wide text-sm">Valet <span className="gold-text">Premier</span></span>
          <Gift className="text-amber-400" size={20} />
        </div>
        <div className="relative">
          <p className="font-mono-gold text-amber-400/70 text-xs tracking-[0.3em]">VPD-••••-••••-••••</p>
          <p className="font-display text-5xl font-extrabold gold-text mt-2" data-testid="gift-preview-amount">${amount}</p>
          {message && <p className="text-zinc-400 text-xs italic mt-3 line-clamp-2">“{message}”</p>}
        </div>
        <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-500 relative">
          <span>To: {recipientName || "Recipient"}</span>
          <span>From: {senderName || "You"}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function GiftCards() {
  const { user } = useAuth();
  const [amount, setAmount] = useState(100);
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", sender_name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkout = async (e) => {
    e.preventDefault();
    if (user === false) {
      toast.error("Please sign in to purchase a gift card.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", { ...form, amount, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (user === false) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 pt-16">
        <div className="gold-border-card p-10 max-w-md text-center" data-testid="gift-auth-required">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-mono-gold mb-3">The Gift of Shine</p>
          <h1 className="font-display font-bold uppercase text-2xl tracking-tight">Sign in to send a gift card</h1>
          <p className="text-zinc-400 text-sm mt-4">A free account keeps your purchased gift cards and codes in one place.</p>
          <Link to="/login?next=/gift-cards" data-testid="gift-signin-link" className="inline-block mt-8 gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3.5 rounded-full">
            Sign In / Create Account
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-testid="gift-cards-page">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-400/90 font-mono-gold mb-3">The Gift of Shine</p>
        <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-5xl">
          Send a <span className="gold-text">gift card</span>
        </h1>
        <p className="text-zinc-400 text-sm mt-4 max-w-lg">A concierge detail, delivered to their driveway. Choose an amount, write a note — we email the golden voucher instantly after checkout.</p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div className="lg:sticky lg:top-28">
          <GiftCardPreview amount={amount} recipientName={form.recipient_name} senderName={form.sender_name} message={form.message} />
        </div>
        <Reveal delay={0.1}>
          <form onSubmit={checkout} className="gold-border-card p-8 space-y-5" data-testid="gift-card-purchase-form">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-zinc-500 block mb-3">Amount</label>
              <div className="grid grid-cols-4 gap-3">
                {GIFT_AMOUNTS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => setAmount(a)}
                    data-testid={`gift-amount-${a}`}
                    className={`py-3.5 rounded-lg border font-display font-bold transition-all ${
                      amount === a ? "border-amber-400/70 bg-amber-500/10 gold-text text-lg" : "border-zinc-800 text-zinc-400 hover:border-amber-500/30"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input className="vpd-input" placeholder="Recipient name" required value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} data-testid="gift-recipient-name" />
              <input className="vpd-input" type="email" placeholder="Recipient email" required value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} data-testid="gift-recipient-email" />
            </div>
            <input className="vpd-input" placeholder="Your name (sender)" required value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="gift-sender-name" />
            <textarea className="vpd-input min-h-[90px]" placeholder="Personal message (optional)" maxLength={300} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="gift-message" />
            <button type="submit" disabled={submitting} data-testid="gift-checkout-button" className="w-full gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs py-4 rounded-full disabled:opacity-50 hover:scale-[1.01] transition-transform">
              {submitting ? "Preparing checkout…" : `Purchase $${amount} Gift Card`}
            </button>
            <p className="text-zinc-600 text-xs text-center">Secure checkout via Stripe. The voucher code is emailed to your recipient.</p>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
